import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Card, CardActionArea, CardContent, Grid, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomFilledButton from "../../components/CustomFilledButton";
import {
  REACT_EMPLOYEE_LOGIN_SCREEN,
  REACT_FUNCTION_KEY_SCREEN,
  REACT_GENERAL_QUESTION,
} from "../../constants/ReactEndPoints";
import { getLaborCodeList, getLaborCodeListPosition } from "../../services/FunctionKeysService";
import CommonUtil from "../../util/CommonUtils";
import { getResultIndexWithParentChildAssociation, isRuleEnabled } from "../../util/helperFunction";
import ShiftDetailsModel from "../../components/ShiftDetailsModel";
import {
  selectCurrentFunctionKey,
  selectEmployeeShiftData,
  selectProvision,
} from "../../feature/Dashboard/dashboardSelectors";
import { useDispatch, useSelector } from "react-redux";
import AppConstant from "../../util/AppConstant";
import { useTranslation } from "react-i18next";

const customTabsStyles = {
  alignItems: "center",
  backgroundColor: "#fff",
  borderRadius: "12px",
  width: "fit-content",
  "& .MuiTabs-root": {
    minHeight: "44px",
  },
  "& .MuiTab-root": {
    minHeight: "36px",
    padding: "8px 12px",
    textTransform: "none",
    fontFamily: "inter",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "20px",
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },

  "& .MuiButtonBase-root.Mui-selected": {
    backgroundColor: "#00A4FF",
    color: "#fff",
    borderRadius: "8px",
  },
};

const useStyles = makeStyles({
  root: {
    minWidth: 220,
    marginTop: "15px",
    borderRadius: "12px",
  },
});

const LabourCode = () => {
  const {t} = useTranslation();
  const [activeCard, setActiveCard] = useState(null);
  const classes = useStyles();
  const location = useLocation();
  const { punchPayload } = location?.state;
  const navigate = useNavigate();
  const [value, setValue] = useState("1");
  const [selectedOrgCodeIndex, setSelectedOrgCodeIndex] = useState(0);
  const [laborCodes, setLaborCodes] = useState([]);
  const [buttonState, setButtonState] = useState(true);
  const [customMappingObj, setCustomMappingObj] = useState({});
  const [feedbackScreenLaborCode, setFeedbackScreenLaborCode] = useState({});
  const [isPositionSkipped, setIsPositionSkipped] = useState(false);
  const [resultIndex, setResultIndex] = useState([]);
  const laborCodeList = JSON.parse(localStorage.getItem("laborCodeData"));
  const [skippedArrIndices, setSkippedArrayIndices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employeeShiftData, setEmployeeShiftData] = useState({});
  const [isShiftLockoutEnable, setIsShiftLockoutEnable] = useState(false);

  const { display_shift_details_on_job_codes_screen: isShowShiftDetailsButton = "false" } =
    useSelector(selectProvision);
  const selectedFunctionKeyObj = useSelector(selectCurrentFunctionKey);
  const storedEmployeeShift = useSelector(selectEmployeeShiftData);

  useEffect(() => {
    if (!CommonUtil.isTruthyValue(localStorage.getItem("isLoggedIn"))) {
      navigate(REACT_EMPLOYEE_LOGIN_SCREEN, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!CommonUtil.isEmpty(storedEmployeeShift)) {
      const selectedShift = storedEmployeeShift.find((shift) => shift.shiftId === punchPayload[0].shiftId);
      if (selectedShift) {
        setEmployeeShiftData(selectedShift);
      }
    }

    if (isRuleEnabled(selectedFunctionKeyObj, AppConstant.SHIFT_LOCKOUT_RULE)) {
      setIsShiftLockoutEnable(true);
    }
  }, [selectedFunctionKeyObj, storedEmployeeShift]);

  useEffect(() => {
    const fetchLaborCodes = async () => {
      try {
        const codes = selectedFunctionKeyObj?.codeLevels?.map((item) => item.code);
        const response = await getLaborCodeList(codes);
        if (response.status === 200 && response.data.message === "Success") {
          const laborCodeData = Array.isArray(response.data.data) ? response.data.data : [];
          const resultIndexes = getResultIndexWithParentChildAssociation(laborCodeData);
          setResultIndex(resultIndexes);

          const positionLaborCode = laborCodeData.filter((item) => item?.masterOrgCodeDetails?.type === "POSITION");

          // Checking the index of the first parent type labor code
          const veryFirstParentLaborCodeIndex = laborCodeData.findIndex((item) =>
            CommonUtil.isEmptyString(item.masterOrgCodeDetails?.parentCodeType)
          );
          if (veryFirstParentLaborCodeIndex >= 0) {
            setSelectedOrgCodeIndex(veryFirstParentLaborCodeIndex);
          }

          let isPosition = positionLaborCode.length > 0;
          let isSingleOrgCodeDetail =
            positionLaborCode.length === 1 && positionLaborCode[0]?.orgCodeDetails?.length === 1;

          // Handle position labor codes
          if (isPosition && isSingleOrgCodeDetail) {
            const positionResponse = await getLaborCodeListPosition();
            if (positionResponse.status === 200 && positionResponse.data.message === "Success") {
              const positionLaborCodeData = Array.isArray(positionResponse.data.data) ? positionResponse.data.data : [];

              //Here map the primary position and collecting it into new potionItemList variable
              const potionItemList = positionLaborCodeData.map((item) => ({
                id: null,
                itemId: item.code,
                itemName: item.title,
                parentCode: "POSITION",
                parentCodeId: null,
                parentItemId: null,
              }));

              //Here replacing the dcs position labor code item with potionItemList(Primary employee position item)
              laborCodeData.forEach((item) => {
                if (item?.masterOrgCodeDetails?.type === "POSITION") {
                  item.orgCodeDetails = potionItemList;
                  item.masterOrgCodeDetails.mappedDataField = "customV7";
                }
              });

              const mappingObj = {
                ...customMappingObj,
                [positionLaborCode[0].masterOrgCodeDetails?.mappedDataField]: potionItemList[0].itemId,
              };
              const mappingObjForFeedbackScreen = {
                ...feedbackScreenLaborCode,
                [positionLaborCode[0].masterOrgCodeDetails?.type]: potionItemList[0].itemId,
              };
              localStorage.setItem("customMappingData", JSON.stringify(mappingObjForFeedbackScreen));
              setFeedbackScreenLaborCode(mappingObjForFeedbackScreen);
              setLaborCodes(laborCodeData);
              setCustomMappingObj(mappingObj);
              localStorage.setItem("laborCodeData", JSON.stringify(laborCodeData || []));

              //skipped the position labor code for one default means  single position item
              if (potionItemList.length === 1) {
                setIsPositionSkipped(true);
              }
            }
          } else {
            setLaborCodes(laborCodeData);
            localStorage.setItem("laborCodeData", JSON.stringify(laborCodeData || []));
          }
        }
      } catch (error) {
        console.error("Error while fetching labor codes:", error);
      }
    };

    fetchLaborCodes();
  }, []);

  //If there is no parent type labor code then navigate to general question page
  useEffect(() => {
    if (CommonUtil.isEmpty(resultIndex) && !CommonUtil.isEmpty(laborCodes)) {
      navigate(REACT_GENERAL_QUESTION, {
        state: {
          punchPayload,
          customMappingObj,
        },
      });
    }
  }, [resultIndex, laborCodes]);

  useEffect(() => {
    let isPositionLaborCode = false;
    let currentSelectedTimeAndLaborCode = {};

    if (!CommonUtil.isEmpty(laborCodes)) {
      currentSelectedTimeAndLaborCode = laborCodes[selectedOrgCodeIndex];
      isPositionLaborCode = currentSelectedTimeAndLaborCode?.masterOrgCodeDetails?.type === "POSITION";
    }

    const lastIndex = laborCodes?.length - 1;
    if (lastIndex === selectedOrgCodeIndex && isPositionLaborCode && isPositionSkipped) {
      const { masterOrgCodeDetails, orgCodeDetails } = currentSelectedTimeAndLaborCode;
      const mappedField = masterOrgCodeDetails.mappedDataField;
      const mappedValue = orgCodeDetails[0].itemId;

      const updatedMappingObj = { ...customMappingObj, [mappedField]: mappedValue };

      setCustomMappingObj(updatedMappingObj);

      navigate(REACT_GENERAL_QUESTION, {
        state: {
          punchPayload,
          customMappingObj,
        },
      });
    } else if (lastIndex !== selectedOrgCodeIndex && isPositionLaborCode && isPositionSkipped) {
      setSelectedOrgCodeIndex(selectedOrgCodeIndex + 1);
      setActiveCard(null);
      setButtonState(true);
    }
  }, [laborCodes, selectedOrgCodeIndex]);

  const handleChange = (event, newValue) => {
    event.preventDefault();
    setValue(newValue);
  };

  const handleClickLabourCode = async (index, detailsObj) => {
    setActiveCard(index);
    setButtonState(false);
    //filter the next child labor code item based on current selected labor code
    const filteredObjectDetails = laborCodeList
      .find((item) => {
        return detailsObj.parentCode === item.masterOrgCodeDetails.parentCodeType;
      })
      ?.orgCodeDetails.filter((item) => {
        return item.parentItemId === detailsObj.id;
      });

    laborCodes.forEach((data) => {
      if (detailsObj.parentCode === data.masterOrgCodeDetails.parentCodeType) {
        data.orgCodeDetails = filteredObjectDetails ? filteredObjectDetails : [];
      }
    });

    const updatedCustomMappingObj = laborCodes.reduce((acc, item) => {
      if (item.masterOrgCodeDetails.type === detailsObj.parentCode) {
        acc[item.masterOrgCodeDetails.mappedDataField] = detailsObj.itemId;
      }
      return acc;
    }, customMappingObj);

    setCustomMappingObj(updatedCustomMappingObj);

    const updatedCustomMappingObjForFeedBackScreen = laborCodes.reduce((acc, item) => {
      if (item.masterOrgCodeDetails.type === detailsObj.parentCode) {
        acc[item.masterOrgCodeDetails.type] = detailsObj.itemId;
      }
      return acc;
    }, feedbackScreenLaborCode);

    setFeedbackScreenLaborCode(updatedCustomMappingObjForFeedBackScreen);
    localStorage.setItem("customMappingData", JSON.stringify(updatedCustomMappingObjForFeedBackScreen));
  };

  const getNextIndexValue = () => {
    const nextIndexFilterResult = resultIndex.filter((item) => skippedArrIndices.indexOf(item) === -1);
    const currentSelectedResultIndex = nextIndexFilterResult.indexOf(selectedOrgCodeIndex);
    let nextIndex = nextIndexFilterResult[currentSelectedResultIndex + 1];

    for (let i = currentSelectedResultIndex + 1; i < nextIndexFilterResult.length; i++) {
      const laborCodeOrgDetails = laborCodes[nextIndex]?.orgCodeDetails;
      //if resultIndex last index and respective labor code item is empty then navigate to General Question page
      if (i === nextIndexFilterResult.length - 1 && CommonUtil.isEmpty(laborCodeOrgDetails)) {
        navigate(REACT_GENERAL_QUESTION, {
          state: {
            punchPayload,
            customMappingObj,
          },
        });
        return null;
      }

      if (CommonUtil.isEmpty(laborCodeOrgDetails)) {
        nextIndex = nextIndexFilterResult[i + 1];
        return nextIndex;
      }
    }
    return nextIndex;
  };

  const handleNextButton = () => {
    const nextButtonFilteredResult = resultIndex.filter((item) => skippedArrIndices.indexOf(item) === -1);
    let currentIndexPosition = nextButtonFilteredResult.indexOf(selectedOrgCodeIndex);
    const lastIndex = nextButtonFilteredResult.length - 1;
    const nextIndexValue = getNextIndexValue();
    if (currentIndexPosition < lastIndex) {
      setSelectedOrgCodeIndex(nextIndexValue);
      setActiveCard(null);
      setButtonState(true);
    } else if (currentIndexPosition === lastIndex) {
      navigate(REACT_GENERAL_QUESTION, {
        state: {
          punchPayload,
          customMappingObj,
        },
      });
    }
  };

  const handleSkipButton = () => {
    let currentIndexPosition = resultIndex.indexOf(selectedOrgCodeIndex);
    let skippedParentCode = laborCodes[selectedOrgCodeIndex]?.masterOrgCodeDetails?.type;
    let skipArr = [...skippedArrIndices];

    for (let i = currentIndexPosition; i < resultIndex.length; i++) {
      const skippedLaborCodeData = laborCodes[i];
      const { masterOrgCodeDetails } = skippedLaborCodeData;

      if (skippedParentCode === masterOrgCodeDetails.parentCodeType) {
        skippedParentCode = masterOrgCodeDetails.type;
        const indexOfChildLaborCode = laborCodes.indexOf(skippedLaborCodeData);
        if (!skipArr.includes(indexOfChildLaborCode)) {
          skipArr.push(indexOfChildLaborCode);
        }
      }
    }
    setSkippedArrayIndices(skipArr);

    // Filter out the skipped parent code from customMappingData
    const customMappingData = JSON.parse(localStorage.getItem("customMappingData") || "{}");
    const filteredCustomMappingData = Object.entries(customMappingData)
      .filter(([key]) => key !== skippedParentCode)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    localStorage.setItem("customMappingData", JSON.stringify(filteredCustomMappingData));
    setFeedbackScreenLaborCode(filteredCustomMappingData);

    // Filter out skipped items from customMappingObj
    const selectedItemToSkip = laborCodes[selectedOrgCodeIndex]?.orgCodeDetails || [];
    const updatedCustomMappingObj = Object.entries(customMappingObj)
      .filter(([key, value]) => !selectedItemToSkip.some((item) => item.itemId === value))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    setCustomMappingObj(updatedCustomMappingObj);

    const filteredResult = resultIndex.filter((item) => skipArr.indexOf(item) === -1);
    currentIndexPosition = filteredResult.indexOf(selectedOrgCodeIndex);

    const lastIndex = filteredResult.length - 1;
    if (currentIndexPosition < lastIndex) {
      const nextIndex = filteredResult[currentIndexPosition + 1];
      if (nextIndex !== undefined) {
        setSelectedOrgCodeIndex(nextIndex);
        setActiveCard(null);
        setButtonState(true);
      }
    } else if (currentIndexPosition === lastIndex) {
      navigate(REACT_GENERAL_QUESTION, {
        state: {
          punchPayload,
          customMappingObj: updatedCustomMappingObj,
        },
      });
    }
  };

  const handleBackButton = () => {
    const filteredResultIndices = resultIndex.filter((item) => skippedArrIndices.indexOf(item) === -1);
    const lastIndexPos = filteredResultIndices.lastIndexOf(selectedOrgCodeIndex);

    if (lastIndexPos > 0) {
      const previousIndex = filteredResultIndices[lastIndexPos - 1];
      let currentIndexPosition = resultIndex.indexOf(previousIndex);
      let skippedParentCode = laborCodes[previousIndex]?.masterOrgCodeDetails?.type;

      for (let i = currentIndexPosition; i < resultIndex.length; i++) {
        const skippedLaborCodeData = laborCodes[i];
        const { masterOrgCodeDetails } = skippedLaborCodeData;

        if (skippedParentCode === masterOrgCodeDetails.parentCodeType) {
          skippedParentCode = masterOrgCodeDetails.type;
          const indexOfChildLaborCode = laborCodes.indexOf(skippedLaborCodeData);
          const index = skippedArrIndices.indexOf(indexOfChildLaborCode);
          if (index !== -1) {
            skippedArrIndices.splice(index, 1);
          }
        }
      }

      setSelectedOrgCodeIndex(previousIndex);
      setActiveCard(null);
      setButtonState(true);
    } else if (lastIndexPos === 0) {
      navigate(REACT_FUNCTION_KEY_SCREEN);
    }
  };

  return (
    !CommonUtil.isEmpty(resultIndex) && (
      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={value}>
          <Box sx={{ ml: "25px", mt: "25px" }}>
            <TabList sx={customTabsStyles} onChange={handleChange} aria-label="lab API tabs example">
              {laborCodes.length > 0 &&
                laborCodes.map((orgCode, index) => (
                  <Tab
                    label={orgCode?.masterOrgCodeDetails?.type}
                    value="1"
                    key={index}
                    style={{
                      backgroundColor: selectedOrgCodeIndex === index ? "#00A4FF" : "inherit",
                      color: selectedOrgCodeIndex === index ? "#fff" : "#A1A1A1",
                      pointerEvents: "none",
                    }}
                    //onClick={() => handleOrgCodeRowClick(index)}
                  />
                ))}
            </TabList>
          </Box>
          <TabPanel value="1">
            <Box>
              <Typography sx={{ fontWeight: "600", fontFamily: "inter" }}>{t("labourCode.title")}</Typography>
            </Box>
            <Grid
              container
              rowSpacing={1}
              columnSpacing={{ xs: 1, sm: 2, md: 3 }}
              sx={{
                height: "300px",
                overflowY: "auto",
                overflowX: "hidden",
                "@media (max-width: 1800px)": { height: "170px" },
                "@media (max-width: 1200px)": { height: "140px" },
              }}
            >
              {laborCodes.length > 0 &&
                laborCodes[selectedOrgCodeIndex].orgCodeDetails.map((detail, index) => (
                  <Grid item sm={4} md={3} lg={3} xl={3} key={index}>
                    <Card
                      sx={{
                        border: activeCard === index ? "1px solid #00A4FF" : "none",
                        boxShadow: activeCard === index ? "0px 4px 10px 2px rgba(0, 0, 0, 0.25)" : "none",
                        borderRadius: "12px",
                      }}
                      onClick={() => handleClickLabourCode(index, detail)}
                      className={classes.root}
                    >
                      <CardActionArea>
                        <CardContent>
                          <Typography
                            sx={{
                              fontSize: "16px",
                              borderBottom: "1px solid",
                              lineHeight: "20px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              pb: "5px",
                              fontFamily: "inter",
                              "@media (max-width: 1800px)": { fontSize: "12px" },
                            }}
                          >
                            {detail.itemId}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              lineHeight: "20px",
                              pt: "10px",
                              fontFamily: "inter",
                              "@media (max-width: 1800px)": { fontSize: "12px" },
                            }}
                            component="p"
                          >
                            {detail.itemName}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}

              <Box sx={{ position: "absolute", left: "15px", bottom: "20px", display: "flex", gap: 2 }}>
                
                <CustomFilledButton 
                onBtnClick={handleBackButton} 
                title={t("labourCode.buttons.back")} 
                width={"150px !important"}
                />

                {isShiftLockoutEnable && !CommonUtil.isEmpty(employeeShiftData) && isShowShiftDetailsButton && (
                  <CustomFilledButton
                    onBtnClick={() => setIsModalOpen(true)}
                    title={t("labourCode.buttons.shiftDetails")}
                    width="150px !important"
                  />
                )}
              </Box>

              <Box sx={{ position: "absolute", right: "15px", bottom: "20px", display: "flex", gap: 2 }}>
                <CustomFilledButton
                  disabled={
                    !selectedFunctionKeyObj?.codeLevels?.find(
                      (item) => item.code === laborCodes[selectedOrgCodeIndex]?.masterOrgCodeDetails?.type
                    )?.skippable ?? true
                  }
                  onBtnClick={() => {
                    handleSkipButton(selectedOrgCodeIndex);
                  }}
                  title={t("labourCode.buttons.skip")}
                  width={"150px !important"}
                />
                <CustomFilledButton
                  disabled={buttonState}
                  onBtnClick={handleNextButton}
                  title={t("labourCode.buttons.next")}
                  width={"150px !important"}
                />
              </Box>
            </Grid>
          </TabPanel>
        </TabContext>

        <ShiftDetailsModel
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          employeeShiftData={employeeShiftData}
        />
      </Box>
    )
  );
};

export default LabourCode;
